import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  doc, getDoc, getDocs, setDoc, collection, serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { OrgContext } from '../../context/OrgContext';
import Login from '../Login';
import Layout from '../Layout';
import Transactions from '../Transactions';
import IncomeExpense from '../Reports/IncomeExpense';
import BalanceSheet from '../Reports/BalanceSheet';
import BudgetByMonth from '../Reports/BudgetByMonth';
import ExportTransactions from '../Reports/ExportTransactions';
import Payees from '../Reports/Payees';
import Form1099 from '../Reports/Form1099';
import Admin from '../Admin';
import Reconcile from '../Reconcile';
import Manager from '../Manager';
import Dashboard from '../Dashboard';

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-gray-500 text-sm">Loading…</div>
  </div>
);

const Unauthorized = ({ onTryAgain }) => (
  <div className="min-h-screen bg-slate-100 flex items-center justify-center">
    <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-sm text-center">
      <div className="text-2xl font-bold text-slate-800 mb-2">Access Denied</div>
      <p className="text-slate-500 text-sm mb-6">
        Your account is not authorized to access any organization.
        Please contact your administrator.
      </p>
      <button onClick={onTryAgain} className="text-sm text-blue-600 hover:underline">
        Try a different account
      </button>
    </div>
  </div>
);

const ClaimSuperAdmin = ({ user }) => {
  const [claiming, setClaiming] = useState(false);
  const [done, setDone] = useState(false);

  const claim = async () => {
    setClaiming(true);
    await setDoc(doc(db, 'superAdmins', user.email), {
      claimedAt: serverTimestamp(),
      displayName: user.displayName || '',
    });
    setDone(true);
  };

  if (done) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-sm text-center">
        <div className="text-xl font-bold text-slate-800 mb-2">You're set up!</div>
        <p className="text-slate-500 text-sm mb-6">Sign out and back in to activate your super admin access.</p>
        <button onClick={() => signOut(auth)} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-sm text-center">
        <div className="text-xl font-bold text-slate-800 mb-2">First-Time Setup</div>
        <p className="text-slate-500 text-sm mb-1">No super admin exists yet.</p>
        <p className="text-slate-500 text-sm mb-6">Claim super admin access for <strong>{user.email}</strong>?</p>
        <button onClick={claim} disabled={claiming} className="w-full bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          {claiming ? 'Claiming…' : 'Claim Super Admin'}
        </button>
      </div>
    </div>
  );
};

const OrgPicker = ({ orgs, onPick }) => (
  <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-2.5 mb-1">
          <svg viewBox="0 0 72 72" className="w-9 h-9 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="0" width="72" height="72" rx="16" fill="#2C2C2A"/>
            <rect x="25" y="20" width="22" height="6" rx="3" fill="#854F0B"/>
            <rect x="18" y="31" width="36" height="7" rx="3" fill="#BA7517"/>
            <rect x="10" y="43" width="52" height="10" rx="3" fill="#EF9F27"/>
          </svg>
          <span className="text-2xl font-bold text-slate-800">Blessio</span>
        </div>
        <p className="text-slate-500 text-sm">Choose an organization to continue.</p>
      </div>
      <div className="space-y-2">
        {orgs.map(org => (
          <button
            key={org.id}
            onClick={() => onPick(org)}
            className="w-full text-left border border-gray-200 rounded-xl px-4 py-3 hover:border-blue-400 hover:bg-blue-50/50 transition-colors group"
          >
            <div className="font-medium text-slate-800 group-hover:text-blue-700">{org.name || org.id}</div>
            <div className="text-xs text-slate-500 mt-0.5">{org.id} · {org.role}</div>
          </button>
        ))}
      </div>
      <button onClick={() => signOut(auth)} className="w-full mt-5 text-sm text-slate-500 hover:text-slate-600 transition-colors">
        Sign out
      </button>
    </div>
  </div>
);

// ── Main App ──────────────────────────────────────────────────────────────────

const App = () => {
  const navigate = useNavigate();
  const [authUser, setAuthUser]         = useState(undefined); // undefined=loading
  const [appUser, setAppUser]           = useState(null);      // enriched user
  const [orgId, setOrgId]               = useState(null);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [screen, setScreen]             = useState('loading');

  // selectOrg defined here so useEffect can reference it via closure safely
  const selectOrg = useCallback((org, user) => {
    const role = org.role;
    localStorage.setItem(`blessio_org_${user.email}`, org.id);
    setOrgId(org.id);
    setAppUser(prev => ({ ...prev, isAdmin: role === 'admin', role }));
    setScreen('app');

    // Record login & ensure org doc exists
    setDoc(doc(db, 'orgs', org.id, 'users', user.email), {
      lastLoginAt: serverTimestamp(),
      displayName: user.displayName || '',
    }, { merge: true });

    setDoc(doc(db, 'orgs', org.id), {
      name: org.name || org.id,
      subdomain: org.id,
      active: true,
    }, { merge: true });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (!u) { setAuthUser(null); setAppUser(null); setScreen('login'); return; }
      setAuthUser(u);

      // First-time bootstrap check
      const allSa = await getDocs(collection(db, 'superAdmins'));
      if (allSa.empty) { setAppUser({ ...u }); setScreen('claim'); return; }

      // Super-admin check
      const saDoc = await getDoc(doc(db, 'superAdmins', u.email));
      const isSuperAdmin = saDoc.exists();

      // Find org memberships by scanning org docs
      const orgsSnap = await getDocs(collection(db, 'orgs'));
      const memberships = [];
      await Promise.all(orgsSnap.docs.map(async (orgDoc) => {
        if (orgDoc.data().active === false) return;
        const userDoc = await getDoc(doc(db, 'orgs', orgDoc.id, 'users', u.email));
        if (userDoc.exists()) {
          memberships.push({ id: orgDoc.id, ...orgDoc.data(), role: userDoc.data().role });
        }
      }));

      const enrichedUser = { ...u, isSuperAdmin };
      setAppUser(enrichedUser);
      setAvailableOrgs(memberships);

      if (memberships.length === 0 && !isSuperAdmin) {
        await signOut(auth);
        setScreen('unauthorized');
        return;
      }

      // Restore last-used org
      const saved = localStorage.getItem(`blessio_org_${u.email}`);
      const savedOrg = memberships.find(o => o.id === saved);

      if (memberships.length === 1) {
        selectOrg(memberships[0], enrichedUser);
      } else if (savedOrg) {
        selectOrg(savedOrg, enrichedUser);
      } else if (memberships.length > 1) {
        setScreen('picker');
      } else {
        // Super admin with no org memberships — go straight to Manager
        setScreen('app');
      }
    });
    return unsubscribe;
  }, [selectOrg]);

  const switchOrg = () => {
    if (availableOrgs.length > 1) setScreen('picker');
  };

  // Super admin "open" a tenant from Manager — switches context
  const openOrg = useCallback((org) => {
    selectOrg({ ...org, role: org.role || 'admin' }, appUser);
    navigate('/transactions');
  }, [selectOrg, appUser, navigate]);

  if (screen === 'loading' && !authUser) return <Loading />;
  if (screen === 'login')        return <Login />;
  if (screen === 'unauthorized') return <Unauthorized onTryAgain={() => { signOut(auth); setScreen('login'); }} />;
  if (screen === 'claim')        return <ClaimSuperAdmin user={appUser} />;
  if (screen === 'picker')       return <OrgPicker orgs={availableOrgs} onPick={(org) => selectOrg(org, appUser)} />;

  return (
    <OrgContext.Provider value={orgId}>
      <Layout user={appUser} availableOrgs={availableOrgs} onSwitchOrg={switchOrg}>
        <Routes>
          {orgId && <>
            <Route path="/" element={<Dashboard />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/reconcile" element={<Reconcile />} />
            <Route path="/reports/income-expense" element={<IncomeExpense />} />
            <Route path="/reports/balance-sheet" element={<BalanceSheet />} />
            <Route path="/reports/budget" element={<BudgetByMonth />} />
            <Route path="/reports/payees" element={<Payees />} />
            <Route path="/reports/export" element={<ExportTransactions />} />
            {appUser?.isAdmin && <Route path="/reports/1099" element={<Form1099 />} />}
            {appUser?.isAdmin && <Route path="/admin" element={<Admin />} />}
          </>}
          {appUser?.isSuperAdmin && (
            <Route path="/manager" element={<Manager onOpenOrg={openOrg} />} />
          )}
          <Route path="*" element={<Navigate to={orgId ? '/transactions' : '/manager'} replace />} />
        </Routes>
      </Layout>
    </OrgContext.Provider>
  );
};

export default App;
